import * as React from "react";
import * as THREE from "three";
import { useGLTF } from "@react-three/drei";
import { GroupProps } from "@react-three/fiber";
import archGlbUrl from "../assets/arch.glb";

type GateProps = React.PropsWithChildren<GroupProps>;
const ARCHWAY_PATH = archGlbUrl;

const DEFAULT_GATE_POSITION: [number, number, number] = [0, -10.4, 0];
const DEFAULT_GATE_SCALE = 9.4; // uniform
// Matches App.js: rotation: [degToRad(0), degToRad(0), degToRad(13)]
const DEFAULT_GATE_ROTATION: [number, number, number] = [
  0,
  0,
  -(13 * Math.PI) / 180,
];

export default function Gate({
  children,
  position = DEFAULT_GATE_POSITION,
  scale = DEFAULT_GATE_SCALE,
  rotation = DEFAULT_GATE_ROTATION,
  ...props
}: GateProps) {
  const { scene } = useGLTF(ARCHWAY_PATH);

  // Upgrade marble material: clearcoat polish + envMap reflections
  React.useEffect(() => {
    scene.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((mat, idx) => {
        if (!mat) return;
        const std = mat as THREE.MeshStandardMaterial;
        const upgraded = new THREE.MeshPhysicalMaterial({
          map: std.map ?? null,
          normalMap: std.normalMap ?? null,
          normalScale: std.normalScale?.clone() ?? new THREE.Vector2(1.5, 1.5),
          roughnessMap: std.roughnessMap ?? null,
          metalnessMap: std.metalnessMap ?? null,
          aoMap: std.aoMap ?? null,
          color: new THREE.Color("#e8e4dd"),
          roughness: 0.3,
          metalness: 0.0,
          clearcoat: 0.5,
          clearcoatRoughness: 0.15,
          envMapIntensity: 1.6,
          sheen: 0.15,
          sheenColor: new THREE.Color("#fff7e6"),
          sheenRoughness: 0.4,
        });
        if (Array.isArray(mesh.material)) {
          (mesh.material as THREE.Material[])[idx] = upgraded;
        } else {
          mesh.material = upgraded;
        }
      });
    });
  }, [scene]);

  return (
    <group position={position} scale={scale} rotation={rotation} {...props}>
      <primitive object={scene} />
      {children}
    </group>
  );
}

useGLTF.preload(ARCHWAY_PATH);
